/**
 * Shared Algolia Filter Utilities
 * 
 * Provides consistent filter clearing functionality across components
 * using the proven ActiveFilters approach
 */

/**
 * Clear all active Algolia refinements
 * Uses the same approach as ActiveFilters component - iterates through
 * individual refinements and calls their refine functions
 * 
 * @param {Array} activeFilters - Array of active refinements from ais-current-refinements
 * @param {Function} sortRefineFunction - Optional sort refine function to reset sort to default
 * @param {string} defaultSortIndex - Default sort index to reset to
 * @returns {boolean} - True if filters were cleared, false if no filters to clear
 */
export const clearAllAlgoliaFilters = (activeFilters, sortRefineFunction = null, defaultSortIndex = null) => {
  console.log('🧹 clearAllAlgoliaFilters called');
  
  let hasCleared = false;
  
  // Clear refinement filters
  if (activeFilters && activeFilters.length > 0) {
    console.log(`🧹 Clearing ${activeFilters.length} filter groups`);
    
    // Use the same approach as ActiveFilters component
    activeFilters.forEach((refinement) => {
      if (refinement.refinements && refinement.refinements.length > 0 && typeof refinement.refine === 'function') {
        console.log(`🧹 Clearing ${refinement.refinements.length} items from ${refinement.attribute}`);
        // Clear each item in this refinement
        refinement.refinements.forEach((item) => {
          refinement.refine(item);
        });
        hasCleared = true;
      }
    });
  }

  // Reset sort to default if sort function is provided
  if (sortRefineFunction && defaultSortIndex) {
    console.log('🧹 Resetting sort to default:', defaultSortIndex);
    sortRefineFunction(defaultSortIndex);
    hasCleared = true;
  }
  
  if (hasCleared) {
    console.log('🧹 All filters and sort cleared successfully');
  } else {
    console.log('🧹 No active filters or sort to clear');
  }
  
  return hasCleared;
};

/**
 * Fallback filter clearing via URL redirect
 * Used when Algolia refinement data is not available
 * 
 * @param {string} basePath - Optional base path, defaults to current pathname
 */
export const clearFiltersViaUrl = (basePath = null) => {
  console.log('🧹 Falling back to URL clear method');
  const currentUrl = new URL(window.location.href);
  const cleanUrl = basePath || `${currentUrl.origin}${currentUrl.pathname}`;
  window.location.href = cleanUrl;
};

/**
 * Universal filter clearing function
 * Tries Algolia method first, falls back to URL method
 * 
 * @param {Array} activeFilters - Array of active refinements from ais-current-refinements  
 * @param {string} fallbackPath - Optional fallback URL path
 * @param {Function} sortRefineFunction - Optional sort refine function to reset sort
 * @param {string} defaultSortIndex - Default sort index to reset to
 */
export const universalClearFilters = (activeFilters, fallbackPath = null, sortRefineFunction = null, defaultSortIndex = null) => {
  const cleared = clearAllAlgoliaFilters(activeFilters, sortRefineFunction, defaultSortIndex);

  if (!cleared) {
    clearFiltersViaUrl(fallbackPath);
  }
};

/**
 * URL Hash Synchronization Utilities
 *
 * Manages URL hash state to prevent mismatches between anchors and active content
 * Used primarily for accordion sections and other interactive content
 */

/**
 * Removes URL hash when it doesn't match the currently active element
 *
 * @param {string} activeElementId - ID of the currently active/opened element
 * @param {boolean} preserveIfMatching - Whether to keep hash if it matches activeElementId (default: true)
 */
export const syncUrlHash = (activeElementId, preserveIfMatching = true) => {
  const currentHash = window.location.hash;
  const expectedHash = activeElementId ? `#${activeElementId}` : '';

  // If there's a hash and it doesn't match the active element, clear it
  if (currentHash && currentHash !== expectedHash) {
    clearUrlHash();
  } else if (preserveIfMatching && activeElementId && !currentHash) {
    // Optionally set hash if active element has ID but no hash exists
    // (Usually not needed - letting native behavior handle initial hash navigation)
  }
};

/**
 * Clears URL hash without page reload or browser history disruption
 *
 * Example use cases:
 * - User navigates to /eng/125/#section-2 (Section 2 opens)
 * - User clicks Section 3 (Section 2 closes, Section 3 opens)
 * - Hash cleared to /eng/125/ (no misleading #section-2)
 */
export const clearUrlHash = () => {
  if (window.location.hash) {
    const url = new URL(window.location);
    url.hash = '';
    // Use replaceState to avoid adding to browser history
    window.history.replaceState(null, '', url.toString());
  }
};

/**
 * Checks if current URL hash matches a specific element ID
 *
 * @param {string} elementId - Element ID to check against (without #)
 * @returns {boolean} - True if hash matches the element ID
 */
export const isHashForElement = (elementId) => {
  const currentHash = window.location.hash;
  return currentHash === `#${elementId}`;
};